/**
 * The Excel tab book: a contents sheet that lists every included table, then
 * the tables themselves, one sheet each or all on one sheet. Each table
 * carries its number, title, base, filter and weight lines above it and its
 * notes below it. Only a table whose run matches the current settings goes
 * in; any other is listed on the contents sheet with its status, never left
 * out. Everything here is pure: the dialog downloads what `buildTabBook`
 * returns.
 */

import ExcelJS from "exceljs"
import type { Font } from "exceljs"
import type { TableModel } from "utils/tableModel"
import type { TableStyle } from "shared/ui/Table/style/tableStyle.types"
import {
  EXCEL_DEFAULT_FONT_NAME,
  tableFreezePane,
  toSheetName,
  writeTableModel,
} from "utils/excel/exportTableModels"
import { meanSummaryStats } from "../compiler/table"
import { ResolvedTable, resolveTable } from "../model/resolve"
import { shownNames, ShownNames } from "../model/shownLabels"
import { summaryName } from "../model/summaries"
import { siblingHeadingRows } from "../runtime/buildResult"
import { selectDisplayStatus, TabSpecState } from "../runtime/tabSpecStore"
import type { TableSpec } from "../types/tabSpecDocument"

/** One included table of the book, as the current settings give it. */
export interface BookTable {
  /** 1-based position among the included tables, as the grid numbers them. */
  number: number
  tableId: string
  title: string
  baseText: string
  /**
   * The filter line: the book filter, then the table's own filter ("Filter:
   * <book filter> and <table filter>"); null when there is neither.
   */
  filterText: string | null
  weightText: string
  /** The table's own note, written last under it. */
  note: string | null
  /** The notes of the result, then the warnings of the compiler. */
  notes: string[]
  status: "done" | "error" | "skipped" | "not-run"
  /** Why the table failed, was skipped or was not run. */
  message: string | null
  /** Set only for a table that is done. */
  model: TableModel | null
}

export const CONTENTS_SHEET = "Contents"
/** The sheet that holds every table when they all go on one sheet. */
export const COMBINED_SHEET = "Tables"

const NOT_RUN = "This table was not run."
const FAILED = "The table could not be run."
/** Empty rows between two tables on the combined sheet. */
const GAP_ROWS = 2
const MUTED = "FF6B7280"
/** Excel's own colour for a link. */
const LINK = "FF0563C1"
const CONTENTS_HEADINGS = ["Table", "Title", "Base", "Status"]
const CONTENTS_WIDTHS = [8, 70, 60, 40]
/** The row of the column headings on the contents sheet; the tables follow it. */
const CONTENTS_HEAD_ROW = 7

/**
 * What a grid summary's rows show, as its title ends once it runs: "Top 2",
 * "Middle", the answer as shown, the net's name, "Mean and standard error",
 * "NPS", "Sum". null for any other table.
 */
const summaryWords = (resolved: ResolvedTable, names: ShownNames): string | null => {
  const { table, summary, question } = resolved
  if (table.kind !== "grid-summary" || !summary) return null
  if (summary.kind !== "mean") {
    const variable = question?.label ?? table.question
    return summaryName(summary, (label) => names.option(variable, label))
  }
  // A summary of means names every statistic it shows, the first as it is.
  const shows = meanSummaryStats(resolved.statistics).map((s, i) =>
    i === 0 ? s.display : s.display.toLowerCase()
  )
  return shows.length === 1
    ? shows[0]
    : `${shows.slice(0, -1).join(", ")} and ${shows[shows.length - 1]}`
}

/**
 * The included tables of the spec, in order. A table takes its model only
 * when its run is done with the current settings; a failed or skipped run
 * that still matches them gives its reason; anything else (never run, out of
 * date, still running) is "not run". The texts come from the plan of a run
 * that still matches the settings; the others show the current title and no
 * base. A grid summary's title names what its rows show ("- Top 2") whether
 * it ran or not: only a plan that ran adds it, so a skipped or not run
 * summary gets it here, worded as the run would word it.
 */
export const collectBook = (state: TabSpecState): BookTable[] => {
  const { document, catalogue } = state
  const names = shownNames(document.labels)
  /** `title` with what the grid summary `resolved` shows after it; any other table's as it is. */
  const withSummary = (resolved: ResolvedTable, title: string): string => {
    const words = summaryWords(resolved, names)
    return words ? `${title} - ${words}` : title
  }
  /** The title of a skipped run: a grid summary's names what it shows. */
  const skippedTitle = (table: TableSpec, title: string): string =>
    table.kind === "grid-summary" && catalogue
      ? withSummary(resolveTable(table, document, catalogue, names), title)
      : title
  return document.tables
    .filter((t) => t.include)
    .map((table, index): BookTable => {
      const common = {
        number: index + 1,
        tableId: table.id,
        note: table.note?.trim() ? table.note : null,
      }
      const run = state.runs[table.id]
      const display = selectDisplayStatus(state, table.id)
      if (run?.plan) {
        const { plan } = run
        const texts = {
          title: plan.title,
          baseText: plan.baseText,
          filterText: plan.filterText,
          weightText: plan.weightText,
          notes: [...run.notes, ...run.warnings],
        }
        if (display === "done" && run.model)
          return { ...common, ...texts, status: "done", message: null, model: run.model }
        if (display === "error")
          return { ...common, ...texts, status: "error", message: run.message ?? FAILED, model: null }
        // A skipped plan keeps the title it had before what the summary shows was added.
        if (display === "skipped")
          return {
            ...common,
            ...texts,
            title: skippedTitle(table, plan.title),
            status: "skipped",
            message: run.message ?? "",
            model: null,
          }
      }
      const resolved = catalogue ? resolveTable(table, document, catalogue, names) : null
      return {
        ...common,
        title: resolved ? withSummary(resolved, resolved.title) : (table.title ?? table.question),
        baseText: "",
        filterText: null,
        weightText: "",
        notes: [],
        status: "not-run",
        message: NOT_RUN,
        model: null,
      }
    })
}

/** Writes a line of text in column `col`. */
const putText = (
  ws: ExcelJS.Worksheet,
  row: number,
  col: number,
  text: string,
  font: Partial<Font> = {}
): void => {
  const cell = ws.getCell(row, col)
  cell.value = text
  cell.font = { name: EXCEL_DEFAULT_FONT_NAME, ...font }
}

/** A link to a cell of this workbook; a quote in the sheet name is doubled. */
const cellLink = (sheet: string, row: number): string =>
  `#'${sheet.replace(/'/g, "''")}'!A${row}`

/**
 * The model the writer gets: without its title. The writer would put the
 * title in the table's corner or on a line above the table, and the block
 * has written it already.
 */
const untitled = (model: TableModel): TableModel => ({ ...model, title: undefined })

/**
 * Writes one table's block from `start`: "Table <n>", the title, the base,
 * the filter when there is one, the weight, an empty row, the table, then
 * the notes (muted) and the table's note. Returns the row the table starts
 * on and the row after the block. A grid from sibling containers shows each
 * sibling's name in bold above its block; in one row per sibling, its bases
 * are the first note.
 */
const writeBlock = (
  ws: ExcelJS.Worksheet,
  table: BookTable,
  model: TableModel,
  style: TableStyle,
  start: number
): { tableRow: number; next: number } => {
  const size = style.font.sizePt
  let row = start
  putText(ws, row++, 1, `Table ${table.number}`, { size, bold: true })
  putText(ws, row++, 1, table.title, { size, bold: true })
  if (table.baseText) putText(ws, row, 1, table.baseText, { size })
  row++
  if (table.filterText) putText(ws, row++, 1, table.filterText, { size })
  if (table.weightText) putText(ws, row++, 1, table.weightText, { size })
  row++
  const tableRow = row
  row = writeTableModel(ws, untitled(model), style, tableRow)
  // A grid from sibling containers, stacked: the row that names each sibling is bold.
  const firstRow = tableFreezePane(untitled(model), tableRow).ySplit + 1
  siblingHeadingRows(model).forEach((r) => {
    const cell = ws.getCell(firstRow + r, 1)
    // A new style: the setter would change a style object the cell may share.
    cell.style = { ...cell.style, font: { ...cell.font, bold: true } }
  })
  table.notes.forEach((text) =>
    putText(ws, row++, 1, text, { size, color: { argb: MUTED } })
  )
  if (table.note) putText(ws, row++, 1, table.note, { size })
  return { tableRow, next: row }
}

/** What the Status column says of a table without a sheet. */
const statusText = (table: BookTable): string | null => {
  if (table.status === "error") return `Failed: ${table.message ?? FAILED}`
  if (table.status === "skipped") return `Skipped: ${table.message ?? ""}`
  if (table.status === "not-run") return "Not run"
  return null
}

const writeContents = (
  ws: ExcelJS.Worksheet,
  args: {
    tables: BookTable[]
    projectName: string
    specName: string
    approval: string
    exportedAt: Date
  },
  links: Map<string, string>
): void => {
  CONTENTS_WIDTHS.forEach((width, i) => {
    ws.getColumn(i + 1).width = width
  })
  putText(ws, 1, 1, args.projectName, { bold: true, size: 14 })
  putText(ws, 2, 1, args.specName, { bold: true })
  putText(ws, 3, 1, `Exported ${args.exportedAt.toLocaleString()}`, {
    color: { argb: MUTED },
  })
  const weight = args.tables.find((t) => t.weightText)?.weightText
  if (weight) putText(ws, 4, 1, weight)
  putText(ws, 5, 1, `Status: ${args.approval}`)
  CONTENTS_HEADINGS.forEach((head, i) =>
    putText(ws, CONTENTS_HEAD_ROW, i + 1, head, { bold: true })
  )
  args.tables.forEach((table, i) => {
    const row = CONTENTS_HEAD_ROW + 1 + i
    const number = ws.getCell(row, 1)
    number.value = table.number
    number.font = { name: EXCEL_DEFAULT_FONT_NAME }
    number.alignment = { horizontal: "left" }
    const link = links.get(table.tableId)
    if (link) {
      const title = ws.getCell(row, 2)
      title.value = { text: table.title, hyperlink: link }
      title.font = {
        name: EXCEL_DEFAULT_FONT_NAME,
        color: { argb: LINK },
        underline: true,
      }
    } else {
      putText(ws, row, 2, table.title)
    }
    if (table.baseText) putText(ws, row, 3, table.baseText)
    const status = statusText(table)
    if (status) putText(ws, row, 4, status)
  })
}

/**
 * The tab book. The contents sheet comes first and lists every table; each
 * table that is done gets a sheet of its own ("separate", frozen at its
 * headers when the style asks for it) or a block on one sheet ("combined",
 * two empty rows between blocks, never frozen). The title of a table with a
 * sheet links to the first row of its block. Without a table that is done
 * the workbook holds the contents sheet only.
 */
export const buildTabBook = (args: {
  tables: BookTable[]
  projectName: string
  specName: string
  approval: string
  style: TableStyle
  mode: "separate" | "combined"
  exportedAt: Date
}): ExcelJS.Workbook => {
  const { tables, style, mode } = args
  const wb = new ExcelJS.Workbook()
  const contents = wb.addWorksheet(CONTENTS_SHEET)
  // "History" is reserved by Excel.
  const used = new Set<string>(["history", CONTENTS_SHEET.toLowerCase()])
  /** Table id to the link of its block. */
  const links = new Map<string, string>()
  const ready = tables.flatMap((t) =>
    t.status === "done" && t.model ? [{ table: t, model: t.model }] : []
  )

  if (mode === "combined") {
    if (ready.length > 0) {
      const ws = wb.addWorksheet(toSheetName(COMBINED_SHEET, 0, used))
      let row = 1
      ready.forEach(({ table, model }, i) => {
        if (i > 0) row += GAP_ROWS
        links.set(table.tableId, cellLink(ws.name, row))
        row = writeBlock(ws, table, model, style, row).next
      })
    }
  } else {
    ready.forEach(({ table, model }, i) => {
      const ws = wb.addWorksheet(toSheetName(`Table ${table.number}`, i, used))
      const { tableRow } = writeBlock(ws, table, model, style, 1)
      if (style.excel.freezePanes)
        ws.views = [{ state: "frozen", ...tableFreezePane(untitled(model), tableRow) }]
      links.set(table.tableId, cellLink(ws.name, 1))
    })
  }

  writeContents(contents, args, links)
  return wb
}

/**
 * The default file name: "<project> - <spec>", each character a file name
 * refuses (\ / : * ? " < > |) replaced by a space and runs of spaces
 * collapsed. An empty part is left out.
 */
export const bookFileName = (projectName: string, specName: string): string =>
  [projectName, specName]
    .map((part) =>
      part
        .replace(/[\\/:*?"<>|]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join(" - ")
